import { createIntegration, getIntegrations, updateIntegration } from 'api/MobileAppsAPI';
import { fetchIntegrations } from 'modules/mobileApps/actions';
import { TOAST_ACTIONS } from 'modules/mobileApps/constants';
import {
  IntegrationFormField,
  useIntegrationFormContext,
} from 'modules/mobileApps/context/integrationForm';
import {
  IntegrationOperationType,
  useIntegrationMetadataContext,
} from 'modules/mobileApps/context/integrationMetadata';
import { ModalType, useModalContext } from 'modules/mobileApps/context/modal';
import { AppPlatform, Integration, IntegrationAuthType } from 'modules/mobileApps/types';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, Button } from 'ui/button';
import { LongArrowRight24 } from 'ui/icons';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { FirebaseIntegrationForm } from '../IntegrationForm/FirebaseIntegrationForm';
import { formatIntegrationFieldsForSave, getIsModalOpen } from './utils';

export const CONFIGURE_ANDROID_INTEGRATION_MODAL_ID = 'configure-android-integration-modal';

type Props = { authType?: IntegrationAuthType };

export const ConfigureAndroidIntegrationModal = ({
  authType = IntegrationAuthType.FIREBASE_JSON,
}: Props) => {
  const dispatch = useDispatch();

  const { closeModal, openModal, openedModals } = useModalContext();
  const { selectedIntegration, operationType, setSelectedIntegration } =
    useIntegrationMetadataContext();
  const { getValues, handleSubmit, setValue, watch } = useIntegrationFormContext();
  const firebaseJsonFile = watch(IntegrationFormField.firebaseJsonFile);
  const isUpdateOperation = operationType === IntegrationOperationType.UPDATE;

  const handleClose = () => {
    closeModal(ModalType.ConfigureAndroidIntegrationModal);
  };

  const handleSave = handleSubmit(async () => {
    if (!selectedIntegration) return;

    try {
      const formFields = getValues();
      const formattedIntegration = await formatIntegrationFieldsForSave(
        formFields,
        selectedIntegration,
        operationType,
        AppPlatform.ANDROID,
      );
      const createOrUpdate = isUpdateOperation ? updateIntegration : createIntegration;
      await createOrUpdate(formattedIntegration);

      // Refresh the integration table to render the new integration row
      dispatch(fetchIntegrations());

      const {
        data: { results: integrations },
      } = await getIntegrations();
      const savedIntegration = (integrations as Integration[])?.find(
        (i) =>
          i.mobileAppId === selectedIntegration.mobileAppId &&
          i.name === selectedIntegration.name &&
          i.authType === selectedIntegration.authType,
      );
      if (savedIntegration) setSelectedIntegration(savedIntegration);

      handleClose();
      openModal(ModalType.TestPushModal, { fileName: firebaseJsonFile?.name });
    } catch (error) {
      dispatch(
        addNegativeToastWithErrorId(
          `Unable to save Firebase integration. Please try again.`,
          TOAST_ACTIONS.testPush,
          error,
        ),
      );
    }
  });

  useEffect(() => {
    if (authType) setValue(IntegrationFormField.authType, authType);
  }, [authType]);

  return (
    <Modal
      data-test={CONFIGURE_ANDROID_INTEGRATION_MODAL_ID}
      id={CONFIGURE_ANDROID_INTEGRATION_MODAL_ID}
      isModalOpen={getIsModalOpen(openedModals, ModalType.ConfigureAndroidIntegrationModal)}
      onClose={handleClose}
      withPortal
    >
      <ModalHeader data-test={`${CONFIGURE_ANDROID_INTEGRATION_MODAL_ID}-header`}>
        Configure Firebase integration
      </ModalHeader>
      <ModalBody>
        <FirebaseIntegrationForm />
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={`${CONFIGURE_ANDROID_INTEGRATION_MODAL_ID}-save-button`}
          onClick={handleSave}
          size={BUTTON_SIZES.LARGE}
        >
          Save
          <LongArrowRight24 />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
